import { runtimeConfig, type RuntimeConfig } from '../../app/config';
import type { Permission } from '../../auth/OidcProvider';

/**
 * The services whose API reference the portal shows - one list, so navigation, overview and routes
 * cannot disagree about which exist.
 *
 * `api` is the key into `runtimeConfig.apis`: the portal-relative prefix the edge strips before
 * forwarding (`/api/dam/` -> dam-server). The document is fetched from `<prefix>openapi/v1.yaml`
 * and every call from the reference goes to the same prefix, so it stays on the portal's origin
 * and needs no CORS. The `servers:` the documents declare are placeholders and are overridden.
 */
export interface ReferencedService {
  id: string;
  title: string;
  repository: string;
  permission: Permission;
  api: keyof RuntimeConfig['apis'];
  /**
   * False where the service has no document route yet. Only there may a refusal (401/403) read as
   * "no description": every service refuses unmapped anonymous requests by default, so such a
   * service answers 401 rather than 404. Anywhere else a refusal means the edge or the service is
   * misconfigured, and the page must say that instead of hiding it.
   */
  servesDocument: boolean;
}

export const referencedServices: readonly ReferencedService[] = Object.freeze([
  { id: 'dam', title: 'Mediendaten', repository: 'dam-server', permission: 'dam.apidocs', api: 'dam', servesDocument: true },
  { id: 'docs', title: 'Dokumente', repository: 'docs-server', permission: 'docs.apidocs', api: 'docs', servesDocument: true },
  { id: 'std', title: 'Stammdaten', repository: 'std-server', permission: 'std.apidocs', api: 'masterdata', servesDocument: true },
  { id: 'mail', title: 'Mailversand', repository: 'mail-service', permission: 'mail.apidocs', api: 'mail', servesDocument: true },
  // No OpenAPI document yet (sftpgo-service/TASKS.md). Listed on purpose: the permission exists,
  // and the page says the service publishes no description rather than the entry being missing.
  { id: 'sftp', title: 'Datentransfer', repository: 'sftpgo-service', permission: 'sftp.apidocs', api: 'sftpgo', servesDocument: false },
]);

export const apiReferencePermissions: readonly Permission[] = referencedServices.map((service) => service.permission);

export function referencedService(id: string | undefined) {
  return referencedServices.find((service) => service.id === id);
}

/** The portal-relative base every call from the reference is sent to. */
export function serviceBase(service: ReferencedService) {
  return runtimeConfig.apis[service.api];
}

export function documentUrl(service: ReferencedService) {
  return new URL('openapi/v1.yaml', serviceBase(service));
}
